import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Eye, EyeOff } from 'lucide-react'
import { trackEvent } from '../lib/analytics'

const Auth = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn, signUp, resendVerification } = useAuth()
  
  // Determine initial mode based on URL path
  const isRegisterPath = location.pathname === '/register'
  const [isLogin, setIsLogin] = useState(!isRegisterPath)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  // CRITICAL: Store only plain strings, never objects
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')

  // "Email not confirmed" resend-verification action (shown inline below the
  // login error). Only rendered when the last login attempt failed because
  // the user has not yet verified their e-mail address.
  const RESEND_COOLDOWN_SECONDS = 30
  const [needsVerification, setNeedsVerification] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const [resendError, setResendError] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)

  // Countdown for the per-tab cooldown after a successful resend.
  useEffect(() => {
    if (resendCooldown <= 0) return
    const id = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => clearInterval(id)
  }, [resendCooldown])

  // Keep mode in sync with the URL when the user navigates between
  // /login and /register without unmounting Auth (e.g. via header links).
  useEffect(() => {
    const shouldBeLogin = location.pathname !== '/register'
    setIsLogin((prev) => {
      if (prev === shouldBeLogin) return prev
      // Mirror the field reset that toggleMode performs.
      setError('')
      setMessage('')
      setPassword('')
      setConfirmPassword('')
      setConfirmPasswordError('')
      setDisplayName('')
      setShowPassword(false)
      setShowConfirmPassword(false)
      setNeedsVerification(false)
      setResendMessage('')
      setResendError('')
      return shouldBeLogin
    })
  }, [location.pathname])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Clear messages (set to empty strings, not null/undefined)
    setError('')
    setMessage('')
    setConfirmPasswordError('')
    setNeedsVerification(false)
    setResendMessage('')
    setResendError('')
    
    // Validate password confirmation for registration
    if (!isLogin && password !== confirmPassword) {
      setConfirmPasswordError('Heslá sa nezhodujú')
      return
    }
    
    setLoading(true)

    try {
      if (isLogin) {
        // LOGIN
        console.log('[Auth] Starting login...')
        
        // Call signIn and DON'T store result
        await signIn(email, password)
        
        console.log('[Auth] Login successful')
        
        // Navigate WITHOUT passing state (to avoid cloning issues)
        const from = location.state?.from || '/'
        navigate(from, { replace: true })
        
      } else {
        // SIGNUP
        console.log('[Auth] Starting signup...')
        trackEvent('signup_started')
        
        // Call signUp and DON'T store result
        await signUp(email, password, displayName)
        
        console.log('[Auth] Signup successful')
        trackEvent('signup_completed')
        
        // Set plain string message
        setMessage('Registrácia úspešná! Poslali sme vám overovací e-mail. Kliknite na odkaz v e-maile a potom sa prihláste.')
        
        // Switch to login
        setIsLogin(true)
        setPassword('')
        setConfirmPassword('')
        setDisplayName('')
      }
    } catch (err) {
      // Extract plain string message directly from error
      const errorMsg = err?.message || 'Nastala chyba. Skúste to prosím znova.'
      console.error('[Auth] Auth failed:', errorMsg)
      
      // CRITICAL: Set only plain string
      setError(errorMsg)
      // Show the inline "Resend verification email" action ONLY when the
      // login error is specifically about the email not being confirmed.
      // Wrong password / unknown user must NOT expose it.
      if (isLogin && err?.code === 'email_not_confirmed') {
        setNeedsVerification(true)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    // Guard against duplicate calls: in-flight request or active cooldown.
    if (resendLoading || resendCooldown > 0) return
    setResendMessage('')
    setResendError('')
    if (!email || !email.trim()) {
      setResendError('Zadajte prosím e-mail, na ktorý ste sa registrovali.')
      return
    }
    setResendLoading(true)
    try {
      await resendVerification(email)
      setResendMessage('Overovací e-mail bol odoslaný. Skontrolujte si doručenú poštu aj priečinok spam.')
      // Start per-tab cooldown so accidental double-clicks do not burn
      // through Supabase's per-email resend rate limit.
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
    } catch (err) {
      setResendError(err?.message || 'Overovací e-mail sa nepodarilo odoslať. Skúste to neskôr alebo nás kontaktujte na podpora@edumisia.sk.')
    } finally {
      setResendLoading(false)
    }
  }

  const toggleMode = () => {
    setIsLogin(!isLogin)
    setError('')
    setMessage('')
    setPassword('')
    setConfirmPassword('')
    setConfirmPasswordError('')
    setDisplayName('')
    setShowPassword(false) // Reset password visibility when switching modes
    setShowConfirmPassword(false) // Reset confirm password visibility
    setNeedsVerification(false)
    setResendMessage('')
    setResendError('')
  }

  // Real-time validation for confirm password
  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(String(value))
    
    // Real-time validation
    if (value && password && value !== password) {
      setConfirmPasswordError('Heslá sa nezhodujú')
    } else {
      setConfirmPasswordError('')
    }
  }

  // Check if passwords match for registration
  const passwordsMatch = isLogin || (password && confirmPassword && password === confirmPassword)
  const canSubmit = isLogin ? true : passwordsMatch && confirmPassword.length >= 6

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900" data-testid="auth-title">
              {isLogin ? 'Prihlásenie' : 'Registrácia'}
            </h2>
            <p className="text-gray-600 mt-2">
              {isLogin ? 'Prihláste sa do svojho účtu' : 'Vytvorte si nový účet'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="error-message">
              <p className="text-red-600 text-sm">{String(error)}</p>
              {needsVerification && (
                <div className="mt-3 pt-3 border-t border-red-100" data-testid="resend-verification-block">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading || resendCooldown > 0}
                    className="text-sm font-semibold text-blue-700 hover:text-blue-800 underline underline-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:no-underline"
                    data-testid="resend-verification-button"
                  >
                    {resendLoading
                      ? 'Odosielam…'
                      : resendCooldown > 0
                        ? `Znova o ${resendCooldown} s`
                        : 'Poslať overovací e-mail znova'}
                  </button>
                  {resendMessage && (
                    <p className="mt-2 text-xs text-green-700" data-testid="resend-verification-success">
                      {resendMessage}
                      {resendCooldown > 0 && (
                        <>
                          {' '}
                          <span className="text-slate-600" data-testid="resend-verification-cooldown">
                            Znova ho môžete odoslať o {resendCooldown} s.
                          </span>
                        </>
                      )}
                    </p>
                  )}
                  {resendError && (
                    <p className="mt-2 text-xs text-red-600" data-testid="resend-verification-error">
                      {resendError}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {message && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="success-message">
              <p className="text-green-600 text-sm">{String(message)}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                  Meno
                </label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(String(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Vaše meno"
                  data-testid="display-name-input"
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(String(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="vas@email.sk"
                data-testid="email-input"
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Heslo
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(String(e.target.value))}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  minLength={6}
                  data-testid="password-input"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? "Skryť heslo" : "Zobraziť heslo"}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Potvrď heslo
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={confirmPassword}
                    onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      confirmPasswordError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="••••••••"
                    minLength={6}
                    data-testid="confirm-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={showConfirmPassword ? "Skryť heslo" : "Zobraziť heslo"}
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {confirmPasswordError && (
                  <p className="mt-2 text-sm text-red-600" data-testid="confirm-password-error">
                    {confirmPasswordError}
                  </p>
                )}
              </div>
            )}

            {/* Forgot Password Link - only for login */}
            {isLogin && (
              <div className="text-right mb-4">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Zabudli ste heslo?
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !canSubmit}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {loading ? 'Spracovávam...' : isLogin ? 'Prihlásiť sa' : 'Registrovať sa'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={toggleMode}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              data-testid="toggle-auth-mode"
              type="button"
            >
              {isLogin ? 'Nemáte účet? Registrujte sa' : 'Už máte účet? Prihláste sa'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Auth
